import { Channel, ChannelMessage, Thread, ThreadUser } from '@schooly/api';
import { useUserNotifications } from '@schooly/context';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Channel as StreamChannel, Event as StreamChatEvent } from 'stream-chat';

import { channelTimestampCache } from './channelTimestampCache';

export type ChannelReadState = {
  lastReadMessageId: string | null;
  unreadCount: number;
  messageCount: number;
  lastMessage: ChannelMessage | null;
};

type UseChannelReadStateProps = {
  selected: boolean;
  canSubscribe: boolean;
  currentChannel: Channel;
  thread: Thread;
  userId: string;
  streamChannel?: StreamChannel;
};

export const useChannelReadState = ({
  currentChannel,
  selected,
  canSubscribe,
  thread,
  userId,
  streamChannel,
}: UseChannelReadStateProps) => {
  const [readState, setReadState] = useState<ChannelReadState>({
    lastReadMessageId: currentChannel.last_read_message_id,
    unreadCount: currentChannel.unread_count,
    messageCount: currentChannel.message_count,
    lastMessage: currentChannel.last_channel_message,
  });

  const subscribedChannel = streamChannel?.id === currentChannel.id ? streamChannel : null;

  const { threadsUnread, onNotificationsUpdate, notificationTimestamp } = useUserNotifications();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  const updateFromSubscription = useCallback(
    (channelInstance: StreamChannel) => {
      const userReadState = channelInstance.state.read[userId];
      if (!userReadState) return;
      channelTimestampCache.updateTimestamp({
        threadId: thread.id,
        channelId: currentChannel.id,
      });

      const messages =
        channelInstance.state.messageSets
          .flatMap((set) => set.messages)
          .filter((m) => m.type !== 'deleted') ?? [];

      const lastChannelMessage = messages[messages.length - 1];

      setReadState({
        lastReadMessageId: userReadState.last_read_message_id ?? null,
        unreadCount: userReadState.unread_messages ?? 0,
        messageCount: messages.length,
        lastMessage: {
          created_at: lastChannelMessage?.created_at.toISOString() ?? '',
          updated_at: lastChannelMessage?.updated_at.toISOString() ?? '',
          sender: (lastChannelMessage?.user as unknown as ThreadUser) ?? null,
          text: lastChannelMessage?.text ?? '',
          status: lastChannelMessage?.status ?? '',
        },
      });
    },
    [userId],
  );

  const channelUnreadReadState = useMemo(
    () =>
      threadsUnread?.unread_threads[thread.root_entity_type]?.[thread.root_entity_id]?.find(
        (c) => c.thread_id === thread.id,
      ),
    [threadsUnread, thread],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  useEffect(() => {
    const handleNotificationUpdate = () => {
      if (selected || !canSubscribe) return;
      const channelUpdateTimestamp = channelTimestampCache.getTimestamp({
        threadId: thread.id,
        channelId: currentChannel.id,
      });

      if (notificationTimestamp < channelUpdateTimestamp) return;

      setReadState((old) => {
        if (!channelUnreadReadState && old) {
          return {
            ...old,
            unreadCount: 0,
          };
        }

        return channelUnreadReadState
          ? {
              lastReadMessageId: channelUnreadReadState.last_read_message_id,
              unreadCount: channelUnreadReadState.unread_count,
              messageCount: channelUnreadReadState.message_count,
              lastMessage: channelUnreadReadState.last_channel_message,
            }
          : old;
      });
    };

    const unsubscribe = onNotificationsUpdate(handleNotificationUpdate);

    handleNotificationUpdate();

    return unsubscribe;
  }, [channelUnreadReadState, notificationTimestamp, onNotificationsUpdate, selected]);

  useEffect(() => {
    if (!subscribedChannel) return;

    const handleEvent = (event: StreamChatEvent) => {
      if (event.channel_id !== subscribedChannel.id) return;
      updateFromSubscription(subscribedChannel);
    };

    const subscription = subscribedChannel.on(handleEvent);

    return subscription.unsubscribe;
  }, [subscribedChannel, updateFromSubscription]);

  return {
    readState,
  };
};
